// Mirrors `workers/collab/src/room-protocol.ts`. The Worker is a separate
// npm package (its own tsconfig, Cloudflare Workers types) so it cannot be
// imported directly from the Vite app; this file is the "single clearly
// marked mirrored definition" instead. Keep the two files in sync by hand:
// any change to the wire shapes or control codes on the server side must be
// re-applied here.

import {
  base64ToString,
  stringToBase64,
  toByteString,
} from "@excalidraw/excalidraw/data/encode";
import { IV_LENGTH_BYTES } from "@excalidraw/excalidraw/data/encryption";
import { IMAGE_MIME_TYPES, MIME_TYPES } from "@excalidraw/common";

import type { FileId } from "@excalidraw/element/types";
import type { BinaryFileData } from "@excalidraw/excalidraw/types";

import { WS_SUBTYPES } from "../app_constants";
import { IMAGE_IMPORT_MAX_BYTES } from "../image-import/config";

export const ROOM_CONTROL_CODES = {
  MEMBER_LIST: "member_list",
  PEER_JOINED: "peer_joined",
  PEER_LEFT: "peer_left",
  ROOM_CLOSING: "room_closing",
} as const;

export const ROOM_CLOSE_REASONS = {
  SESSION_EXPIRED: "session_expired",
} as const;

/**
 * What a room allows the people who joined it by link to do with the drawing
 * once the session is over. Mirrors `RetainPolicy` in
 * `workers/collab/src/retain-policy.ts`.
 *
 * The Worker only carries the value; honouring it is entirely this client's
 * job. It is chosen once by whoever opens the room and is fixed for the
 * room's life, so there is no endpoint (and must be no UI) to change it.
 */
export const RETAIN_POLICIES = {
  DISCARD: "discard",
  RETAIN: "retain",
} as const;

export type RetainPolicy = typeof RETAIN_POLICIES[keyof typeof RETAIN_POLICIES];

/**
 * Narrows one wire value to a retain policy, answering `"discard"` for
 * anything else.
 *
 * It never rejects the message the value arrived in. A Worker older than this
 * client sends no policy at all, and `member_list` is also what carries the
 * member list and this client's own id, so refusing to parse it would take
 * live collaboration down instead of degrading. `"discard"` is both the
 * server's own default and the safer of the two answers, which makes it the
 * right thing to assume when we were not told.
 */
export const parseRetainPolicy = (value: unknown): RetainPolicy =>
  value === RETAIN_POLICIES.RETAIN
    ? RETAIN_POLICIES.RETAIN
    : RETAIN_POLICIES.DISCARD;

export type RelayDurabilityV1 = "durable" | "volatile";

/** Defines the opaque encrypted packet sent to the room. */
export interface ClientRelayEnvelopeV1 {
  durability: RelayDurabilityV1;
  kind: "relay";
  payload: string;
  version: 1;
}

/** Defines the exact versioned packet delivered to every other room client. */
export interface RelayEnvelopeV1 extends ClientRelayEnvelopeV1 {
  senderClientId: string;
}

/** Reports one peer connection to existing room members. */
export interface PeerJoinedControlV1 {
  clientId: string;
  code: "peer_joined";
  kind: "control";
  version: 1;
}

/** Reports one disconnected peer to remaining room members. */
export interface PeerLeftControlV1 {
  clientId: string;
  code: "peer_left";
  kind: "control";
  version: 1;
}

/** Gives one client its identity and the complete current member IDs. */
export interface MemberListControlV1 {
  code: "member_list";
  kind: "control";
  members: string[];
  /**
   * The room's retain policy. Authoritative: it is built from the room's own
   * persisted session rather than from whoever opened it, every peer receives
   * it, and it is re-sent on every membership change. It therefore wins over
   * the copy that rides on the HTTP grant.
   */
  retainPolicy: RetainPolicy;
  selfClientId: string;
  version: 1;
}

/** Announces the stable reason before the relay closes every connection. */
export interface RoomClosingControlV1 {
  code: "room_closing";
  kind: "control";
  reason: "session_expired";
  version: 1;
}

export type RoomControlEnvelopeV1 =
  | MemberListControlV1
  | PeerJoinedControlV1
  | PeerLeftControlV1
  | RoomClosingControlV1;

export type RoomServerEnvelopeV1 = RelayEnvelopeV1 | RoomControlEnvelopeV1;

/** Narrows parsed JSON to an object with string keys. */
const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const isStringArray = (value: unknown): value is string[] =>
  Array.isArray(value) && value.every((item) => typeof item === "string");

/**
 * Validates one relay envelope coming from the server. The server derives
 * `senderClientId` itself, so this only has to check shape, not authenticity.
 */
const parseRelayEnvelope = (
  value: Record<string, unknown>,
): RelayEnvelopeV1 | null => {
  const expectedKeys = [
    "durability",
    "kind",
    "payload",
    "senderClientId",
    "version",
  ];
  const keys = Object.keys(value).sort();
  const hasExactKeys =
    keys.length === expectedKeys.length &&
    keys.every((key, index) => key === expectedKeys[index]);
  if (
    !hasExactKeys ||
    value.version !== 1 ||
    value.kind !== "relay" ||
    (value.durability !== "durable" && value.durability !== "volatile") ||
    typeof value.payload !== "string" ||
    typeof value.senderClientId !== "string" ||
    value.senderClientId.length === 0
  ) {
    return null;
  }
  return {
    version: 1,
    kind: "relay",
    durability: value.durability,
    payload: value.payload,
    senderClientId: value.senderClientId,
  };
};

/** Validates one control envelope coming from the server against its exact per-code shape. */
const parseControlEnvelope = (
  value: Record<string, unknown>,
): RoomControlEnvelopeV1 | null => {
  if (value.version !== 1 || value.kind !== "control") {
    return null;
  }
  switch (value.code) {
    case ROOM_CONTROL_CODES.PEER_JOINED:
    case ROOM_CONTROL_CODES.PEER_LEFT:
      return typeof value.clientId === "string" && value.clientId.length > 0
        ? {
            version: 1,
            kind: "control",
            code: value.code,
            clientId: value.clientId,
          }
        : null;
    case ROOM_CONTROL_CODES.MEMBER_LIST:
      return isStringArray(value.members) &&
        typeof value.selfClientId === "string" &&
        value.selfClientId.length > 0
        ? {
            version: 1,
            kind: "control",
            code: ROOM_CONTROL_CODES.MEMBER_LIST,
            members: value.members,
            retainPolicy: parseRetainPolicy(value.retainPolicy),
            selfClientId: value.selfClientId,
          }
        : null;
    case ROOM_CONTROL_CODES.ROOM_CLOSING:
      return value.reason === ROOM_CLOSE_REASONS.SESSION_EXPIRED
        ? {
            version: 1,
            kind: "control",
            code: ROOM_CONTROL_CODES.ROOM_CLOSING,
            reason: ROOM_CLOSE_REASONS.SESSION_EXPIRED,
          }
        : null;
    default:
      return null;
  }
};

/**
 * Validates one raw WebSocket text message from the room relay. This is the
 * network boundary: everything arriving here is treated as hostile until it
 * matches one of the server's exact documented shapes.
 */
export const parseRoomServerEnvelope = (
  message: string,
): RoomServerEnvelopeV1 | null => {
  try {
    const value: unknown = JSON.parse(message);
    if (!isRecord(value)) {
      return null;
    }
    return value.kind === "relay"
      ? parseRelayEnvelope(value)
      : value.kind === "control"
      ? parseControlEnvelope(value)
      : null;
  } catch {
    return null;
  }
};

// -----------------------------------------------------------------------------
// App-only extension: not part of the server contract. `RoomDO` never reads
// the decrypted payload, so this app defines its own small discriminated
// message to carry "follow another user" intent through the same encrypted
// relay channel that scene/pointer updates already use. This replaces the
// old socket.io `user-follow` event, which the new dumb relay has no
// equivalent for, and which used to be sent unencrypted (see DRAWOLA plan
// doc, section 4: that leaked the followed username in the clear).
// -----------------------------------------------------------------------------

export const USER_FOLLOW_RELAY_KIND = "drawola/user-follow";

export interface UserFollowRelayMessageV1 {
  kind: typeof USER_FOLLOW_RELAY_KIND;
  action: "FOLLOW" | "UNFOLLOW";
  /** clientId of the peer the sender wants to follow. */
  targetClientId: string;
}

/** Validates one decrypted relay payload as a follow/unfollow message. */
export const isUserFollowRelayMessage = (
  value: unknown,
): value is UserFollowRelayMessageV1 =>
  isRecord(value) &&
  value.kind === USER_FOLLOW_RELAY_KIND &&
  (value.action === "FOLLOW" || value.action === "UNFOLLOW") &&
  typeof value.targetClientId === "string" &&
  value.targetClientId.length > 0;

export const COLLAB_FILE_MAX_BYTES = IMAGE_IMPORT_MAX_BYTES;

const COLLAB_FILE_ID_PATTERN = /^[A-Za-z0-9_-]{1,128}$/;
const COLLAB_REQUEST_ID_PATTERN = /^[A-Za-z0-9_-]{1,192}$/;
const MAX_REQUESTED_FILE_IDS = 100;
const ALLOWED_FILE_MIME_TYPES = new Set<string>([
  ...Object.values(IMAGE_MIME_TYPES),
  MIME_TYPES.binary,
]);

// A file ID becomes a plain-object key later on (`BinaryFiles` in
// `packages/excalidraw`, see `App.addMissingFiles`), and that code assigns
// it with bracket notation (`nextFiles[fileData.id] = fileData`). A peer who
// only needs the shared room key could otherwise pick one of these reserved
// names as its file ID: `__proto__` reassigns the object's own prototype
// through the inherited accessor, and `constructor`/`prototype` shadow
// inherited properties other code may rely on. None of these are legitimate
// file IDs (the app always generates them itself), so they are rejected here
// at the network boundary rather than trusted to stay "merely unlikely".
const RESERVED_OBJECT_KEYS = new Set(["__proto__", "constructor", "prototype"]);

const isSafeFileId = (value: string): boolean =>
  COLLAB_FILE_ID_PATTERN.test(value) && !RESERVED_OBJECT_KEYS.has(value);

export type CollabFileValidationFailure = "malformed" | "oversized";

export type CollabFileValidationResult =
  | { valid: true; file: BinaryFileData }
  | { valid: false; reason: CollabFileValidationFailure };

export interface CollabFilePushMessageV1 {
  type: WS_SUBTYPES.FILE_PUSH;
  payload: { file: BinaryFileData };
}

export interface CollabFileRequestMessageV1 {
  type: WS_SUBTYPES.FILE_REQUEST;
  payload: { requestId: string; fileIds: FileId[] };
}

export interface CollabFileResponseMessageV1 {
  type: WS_SUBTYPES.FILE_RESPONSE;
  payload: {
    requestId: string;
    targetClientId: string;
    file: BinaryFileData;
  };
}

export type CollabFileMessageV1 =
  | CollabFilePushMessageV1
  | CollabFileRequestMessageV1
  | CollabFileResponseMessageV1;

/** Checks that an object contains exactly the allowed keys. */
const hasOnlyKeys = (
  value: Record<string, unknown>,
  requiredKeys: readonly string[],
  optionalKeys: readonly string[] = [],
): boolean => {
  const keys = Object.keys(value);
  return (
    requiredKeys.every((key) => Object.hasOwn(value, key)) &&
    keys.every(
      (key) => requiredKeys.includes(key) || optionalKeys.includes(key),
    )
  );
};

/** Calculates decoded bytes after confirming a canonical base64 payload. */
const getBase64DecodedBytes = (value: string): number | null => {
  if (
    value.length === 0 ||
    value.length % 4 !== 0 ||
    !/^[A-Za-z0-9+/]*={0,2}$/.test(value) ||
    value.slice(0, -2).includes("=")
  ) {
    return null;
  }
  const padding = value.endsWith("==") ? 2 : value.endsWith("=") ? 1 : 0;
  return (value.length / 4) * 3 - padding;
};

// A file's declared `mimeType` is only a string in the payload; nothing
// otherwise stops a peer from labelling arbitrary bytes as a format they are
// not. This app only ever loads these bytes through `new Image()` (see
// `packages/element/src/image.ts`), which never executes script content
// even for a genuine `image/svg+xml`, so a mismatch cannot run code here.
// It can still make an image fail to decode for every other peer with a
// misleading error, so the declared type is cross-checked against the
// file's actual header bytes anyway, rather than trusting it unverified.

/** Reads only the bytes needed for a magic-number check, not the whole (up to 2 MiB) payload. */
const getDecodedHeaderBytes = (
  encoded: string,
  maxBytes: number,
): Uint8Array => {
  const prefixLength = Math.floor(Math.min(encoded.length, maxBytes) / 4) * 4;
  const byteString = base64ToString(encoded.slice(0, prefixLength), true);
  const bytes = new Uint8Array(byteString.length);
  for (let index = 0; index < byteString.length; index += 1) {
    bytes[index] = byteString.charCodeAt(index);
  }
  return bytes;
};

const startsWithBytes = (bytes: Uint8Array, signature: number[]): boolean =>
  bytes.length >= signature.length &&
  signature.every((byte, index) => bytes[index] === byte);

// Longest header any check below reads: the WEBP/AVIF container checks look
// as far as byte offset 11.
const HEADER_BYTES_NEEDED = 12;
const HEADER_BASE64_CHARS_NEEDED = Math.ceil(HEADER_BYTES_NEEDED / 3) * 4;

/**
 * Magic-number signature for each raster format this app accepts. `svg` and
 * `binary` (the app's own "format unknown" bucket, see
 * `packages/element/src/image.ts`) are checked separately below since
 * neither has a fixed byte signature.
 */
const RASTER_FILE_SIGNATURES: Partial<
  Record<string, (bytes: Uint8Array) => boolean>
> = {
  [IMAGE_MIME_TYPES.png]: (bytes) =>
    startsWithBytes(bytes, [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a]),
  [IMAGE_MIME_TYPES.jpg]: (bytes) => startsWithBytes(bytes, [0xff, 0xd8, 0xff]),
  [IMAGE_MIME_TYPES.jfif]: (bytes) =>
    startsWithBytes(bytes, [0xff, 0xd8, 0xff]),
  [IMAGE_MIME_TYPES.gif]: (bytes) =>
    startsWithBytes(bytes, [0x47, 0x49, 0x46, 0x38]) && // "GIF8"
    (bytes[4] === 0x37 || bytes[4] === 0x39) && // "7" or "9"
    bytes[5] === 0x61, // "a"
  [IMAGE_MIME_TYPES.webp]: (bytes) =>
    startsWithBytes(bytes, [0x52, 0x49, 0x46, 0x46]) && // "RIFF"
    bytes.length >= 12 &&
    bytes[8] === 0x57 && // "W"
    bytes[9] === 0x45 && // "E"
    bytes[10] === 0x42 && // "B"
    bytes[11] === 0x50, // "P"
  [IMAGE_MIME_TYPES.bmp]: (bytes) => startsWithBytes(bytes, [0x42, 0x4d]), // "BM"
  [IMAGE_MIME_TYPES.ico]: (bytes) =>
    startsWithBytes(bytes, [0x00, 0x00, 0x01, 0x00]),
  [IMAGE_MIME_TYPES.avif]: (bytes) =>
    bytes.length >= 12 &&
    // ISO-BMFF "ftyp" box; the brand itself is not checked further, since
    // ruling out every other format in this set is already the goal here.
    bytes[4] === 0x66 &&
    bytes[5] === 0x74 &&
    bytes[6] === 0x79 &&
    bytes[7] === 0x70,
};

/** Loose sanity check that decoded bytes are XML-ish text, not binary image data. */
const looksLikeSvgText = (bytes: Uint8Array): boolean => {
  let start = 0;
  if (bytes[0] === 0xef && bytes[1] === 0xbb && bytes[2] === 0xbf) {
    start = 3; // skip a UTF-8 byte-order mark
  }
  while (
    start < bytes.length &&
    (bytes[start] === 0x20 ||
      bytes[start] === 0x09 ||
      bytes[start] === 0x0a ||
      bytes[start] === 0x0d)
  ) {
    start += 1; // skip leading whitespace
  }
  return bytes[start] === 0x3c; // '<'
};

/** Validates one file before it enters or leaves the encrypted room channel. */
export const validateCollabFile = (
  value: unknown,
): CollabFileValidationResult => {
  if (
    !isRecord(value) ||
    !hasOnlyKeys(
      value,
      ["created", "dataURL", "id", "mimeType"],
      ["lastRetrieved", "version"],
    ) ||
    typeof value.id !== "string" ||
    !isSafeFileId(value.id) ||
    typeof value.mimeType !== "string" ||
    !ALLOWED_FILE_MIME_TYPES.has(value.mimeType) ||
    typeof value.dataURL !== "string" ||
    typeof value.created !== "number" ||
    !Number.isFinite(value.created) ||
    value.created < 0 ||
    (value.lastRetrieved !== undefined &&
      (typeof value.lastRetrieved !== "number" ||
        !Number.isFinite(value.lastRetrieved) ||
        value.lastRetrieved < 0)) ||
    (value.version !== undefined &&
      (typeof value.version !== "number" ||
        !Number.isInteger(value.version) ||
        value.version < 0))
  ) {
    return { valid: false, reason: "malformed" };
  }

  const prefix = `data:${value.mimeType};base64,`;
  if (!value.dataURL.startsWith(prefix)) {
    return { valid: false, reason: "malformed" };
  }
  const encoded = value.dataURL.slice(prefix.length);
  if (encoded.length > 4 * Math.ceil(COLLAB_FILE_MAX_BYTES / 3)) {
    return { valid: false, reason: "oversized" };
  }
  const decodedBytes = getBase64DecodedBytes(encoded);
  if (decodedBytes === null) {
    return { valid: false, reason: "malformed" };
  }
  if (decodedBytes > COLLAB_FILE_MAX_BYTES) {
    return { valid: false, reason: "oversized" };
  }

  const headerBytes = getDecodedHeaderBytes(
    encoded,
    HEADER_BASE64_CHARS_NEEDED,
  );
  const signatureCheck = RASTER_FILE_SIGNATURES[value.mimeType];
  if (signatureCheck) {
    if (!signatureCheck(headerBytes)) {
      return { valid: false, reason: "malformed" };
    }
  } else if (
    value.mimeType === IMAGE_MIME_TYPES.svg &&
    !looksLikeSvgText(headerBytes)
  ) {
    return { valid: false, reason: "malformed" };
  }

  const file: BinaryFileData = {
    id: value.id as FileId,
    mimeType: value.mimeType as BinaryFileData["mimeType"],
    dataURL: value.dataURL as BinaryFileData["dataURL"],
    created: value.created,
    ...(value.lastRetrieved === undefined
      ? {}
      : { lastRetrieved: value.lastRetrieved }),
    ...(value.version === undefined ? {} : { version: value.version }),
  };
  return { valid: true, file };
};

/** Validates one decrypted app-level file message at the hostile network boundary. */
export const parseCollabFileMessage = (
  value: unknown,
): CollabFileMessageV1 | null => {
  if (
    !isRecord(value) ||
    !hasOnlyKeys(value, ["payload", "type"]) ||
    !isRecord(value.payload)
  ) {
    return null;
  }
  switch (value.type) {
    case WS_SUBTYPES.FILE_PUSH: {
      if (!hasOnlyKeys(value.payload, ["file"])) {
        return null;
      }
      const result = validateCollabFile(value.payload.file);
      return result.valid
        ? { type: WS_SUBTYPES.FILE_PUSH, payload: { file: result.file } }
        : null;
    }
    case WS_SUBTYPES.FILE_REQUEST: {
      const { fileIds, requestId } = value.payload;
      if (
        !hasOnlyKeys(value.payload, ["fileIds", "requestId"]) ||
        typeof requestId !== "string" ||
        !COLLAB_REQUEST_ID_PATTERN.test(requestId) ||
        !Array.isArray(fileIds) ||
        fileIds.length === 0 ||
        fileIds.length > MAX_REQUESTED_FILE_IDS ||
        !fileIds.every(
          (fileId) => typeof fileId === "string" && isSafeFileId(fileId),
        ) ||
        new Set(fileIds).size !== fileIds.length
      ) {
        return null;
      }
      return {
        type: WS_SUBTYPES.FILE_REQUEST,
        payload: { requestId, fileIds: fileIds as FileId[] },
      };
    }
    case WS_SUBTYPES.FILE_RESPONSE: {
      const { file, requestId, targetClientId } = value.payload;
      if (
        !hasOnlyKeys(value.payload, ["file", "requestId", "targetClientId"]) ||
        typeof requestId !== "string" ||
        !COLLAB_REQUEST_ID_PATTERN.test(requestId) ||
        typeof targetClientId !== "string" ||
        !COLLAB_REQUEST_ID_PATTERN.test(targetClientId)
      ) {
        return null;
      }
      const result = validateCollabFile(file);
      return result.valid
        ? {
            type: WS_SUBTYPES.FILE_RESPONSE,
            payload: { requestId, targetClientId, file: result.file },
          }
        : null;
    }
    default:
      return null;
  }
};

// -----------------------------------------------------------------------------
// Wire encoding of `ClientRelayEnvelopeV1.payload` / `RelayEnvelopeV1.payload`.
// The server contract keeps this opaque ("the existing AES-GCM IV +
// ciphertext encoding"); this app encodes it as base64 of `iv || ciphertext`
// concatenated, reusing the same base64 helpers the rest of the app already
// uses for encrypted data (see `packages/excalidraw/data/blob.ts`).
// -----------------------------------------------------------------------------

/** Concatenates the AES-GCM IV and ciphertext into one base64 wire string. */
export const encodeRelayPayload = (
  iv: Uint8Array,
  ciphertext: ArrayBuffer,
): string => {
  const combined = new Uint8Array(iv.length + ciphertext.byteLength);
  combined.set(iv, 0);
  combined.set(new Uint8Array(ciphertext), iv.length);
  return stringToBase64(toByteString(combined), true);
};

/** Splits one base64 wire string back into its AES-GCM IV and ciphertext. */
export const decodeRelayPayload = (
  payload: string,
): { iv: Uint8Array<ArrayBuffer>; ciphertext: ArrayBuffer } => {
  // Deliberately not `base64ToArrayBuffer` from the shared encode helpers:
  // under Node (this app's own test runner included) that function returns
  // `Buffer.from(base64, "base64").buffer`, which is the shared, larger
  // *pool* buffer Node allocates small `Buffer`s from, not the slice that
  // actually holds this data. `base64ToString(..., true)` only ever calls
  // `atob`, which has no such pooling and works the same in Node and the
  // browser.
  const byteString = base64ToString(payload, true);
  const combined = new Uint8Array(byteString.length);
  for (let index = 0; index < byteString.length; index += 1) {
    combined[index] = byteString.charCodeAt(index);
  }
  const iv = combined.slice(0, IV_LENGTH_BYTES) as Uint8Array<ArrayBuffer>;
  const ciphertext = combined.slice(IV_LENGTH_BYTES).buffer;
  return { iv, ciphertext };
};
